import { useEffect, useRef } from "react";

const FALLBACK_MS = 5 * 60 * 1000;

// Server-relative delays tolerate wrong device clocks. Focus/resume catches a
// suspended phone/tab; the fallback catches edits made from another screen.
export function useTimeRefresh(refresh, timing) {
  const callback = useRef(refresh);
  callback.current = refresh;
  const next = timing?.next_transition_at || "";
  const serverNow = timing?.server_now || timing?.time_evaluated_at || "";
  useEffect(() => {
    let stopped = false;
    let pending = false;
    let timer;
    const run = async () => {
      if (stopped || pending || document.visibilityState === "hidden") return;
      pending = true;
      try { await callback.current(); } catch { /* Existing loaders present errors. */ }
      finally { pending = false; }
    };
    const tick = () => {
      run();
      timer = window.setTimeout(tick, FALLBACK_MS);
    };
    const delta = Date.parse(next) - Date.parse(serverNow);
    timer = window.setTimeout(tick, Number.isFinite(delta) ? Math.max(250, Math.min(delta, FALLBACK_MS)) : FALLBACK_MS);
    window.addEventListener("focus", run);
    window.addEventListener("pageshow", run);
    document.addEventListener("visibilitychange", run);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.removeEventListener("focus", run);
      window.removeEventListener("pageshow", run);
      document.removeEventListener("visibilitychange", run);
    };
  }, [next, serverNow]);
}
